import { createStore, useStore } from '@w/react-store'
import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import {
  createAudioController,
  type AudioController,
  type AudioControllerState,
  type CreateAudioControllerOptions,
} from './audio-controller'

export interface UseAudioOptions {
  createController?: () => AudioController
  controllerOptions?: CreateAudioControllerOptions
}

type UseAudioBaseConfig = {
  onEnd?: () => void
  initialProgress?: number
  initialVolume?: number
  autoplay?: boolean
}

type UrlSourceConfig = UseAudioBaseConfig & {
  url: string | null
  arrayBuffer?: undefined
}

type BufferSourceConfig = UseAudioBaseConfig & {
  arrayBuffer: ArrayBuffer
  url?: undefined
}

export type UseAudioConfig = UrlSourceConfig | BufferSourceConfig | undefined

export interface UseAudioResult {
  status: AudioControllerState['state']
  volume: number
  progress: number
  playing: boolean
  ready: boolean
  duration: number
  play: () => Promise<void>
  pause: () => void
  setProgress: (progress: number) => void
  setRelativeProgress: (delta: number) => void
  setVolume: (volume: number) => void
}

function isReadyState(state: AudioControllerState['state']): boolean {
  return (
    state === 'ready' ||
    state === 'playing' ||
    state === 'paused' ||
    state === 'ended' ||
    state === 'blocked'
  )
}

function useStableController(options?: UseAudioOptions): AudioController {
  const [controller] = useState(() => {
    const create =
      options?.createController ??
      (() => createAudioController(options?.controllerOptions))
    return create()
  })
  return controller
}

export function useAudio(
  config?: UseAudioConfig,
  options?: UseAudioOptions,
): UseAudioResult {
  const controller = useStableController(options)

  const url = config && 'url' in config ? config.url : undefined
  const arrayBuffer =
    config && 'arrayBuffer' in config ? config.arrayBuffer : undefined
  const onEnd = config?.onEnd
  const initialProgress = config?.initialProgress
  const initialVolume = config?.initialVolume
  const autoplay = config?.autoplay

  const onEndRef = useRef(onEnd)
  // oxlint-disable-next-line rc/refs -- Safe to update ref during render for callback refs pattern; ensures latest callback is always used without triggering re-renders
  onEndRef.current = onEnd

  useEffect(() => {
    controller.setOnEnd(() => {
      onEndRef.current?.()
    })

    if (arrayBuffer) {
      controller.load(
        { kind: 'arrayBuffer', arrayBuffer },
        {
          initialProgressMs:
            typeof initialProgress === 'number' ? initialProgress : undefined,
          initialVolume:
            typeof initialVolume === 'number' ? initialVolume : undefined,
          autoplay,
        },
      )
    } else {
      controller.load(url ? { kind: 'url', url } : undefined, {
        initialProgressMs:
          typeof initialProgress === 'number' ? initialProgress : undefined,
        initialVolume:
          typeof initialVolume === 'number' ? initialVolume : undefined,
        autoplay,
      })
    }

    return () => {
      controller.setOnEnd(undefined)
      controller.dispose()
    }
  }, [controller, url, arrayBuffer, initialProgress, initialVolume, autoplay])

  const [audioStore] = useState(() => createStore(controller.getSnapshot()))
  useLayoutEffect(() => {
    const update = () => { audioStore.update(controller.getSnapshot()) }
    const unsubscribe = controller.subscribe(update)
    update()
    return unsubscribe
  }, [controller, audioStore])
  const snapshot = useStore(audioStore)

  return {
    status: snapshot.state,
    volume: snapshot.volume,
    progress: snapshot.progress,
    playing: snapshot.state === 'playing',
    ready: isReadyState(snapshot.state),
    duration: snapshot.duration,
    play: () => controller.play(),
    pause: () => {
      controller.pause()
    },
    setProgress: (progress: number) => {
      controller.seekTo(progress)
    },
    setRelativeProgress: (delta: number) => {
      controller.seekBy(delta)
    },
    setVolume: (volume: number) => {
      controller.setVolume(volume)
    },
  }
}
